'use client';

import {
  cloneElement,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import {
  IconButton,
  type IconButtonProps,
} from '../../../core-components/IconButton';
import { Icon, Skeleton } from '../../../core-components';
import { twJoin, twMerge } from 'tailwind-merge';
import {
  DropdownMenu,
  DropdownMenuOption,
} from '@tui-cruises/mein-schiff-web-react-component-library';

export type InteractionHeaderButtonProps = {
  /** Icon name to display */
  iconName: IconButtonProps['iconName'];
  /** Button variant (primary, secondary, etc.) */
  variant?: IconButtonProps['variant'];
  /** Button state (enabled, disabled, etc.) */
  on?: IconButtonProps['on'];
  /** Tooltip/label text */
  label?: string;
  /** Badge text or count to display next to the label */
  badge?: string;
  /** Children element (link, button, etc.) that handles the action */
  children: ReactElement;
};

export type InteractionHeaderProps = {
  /** Fallback mode - shows skeleton loaders */
  fallback?: boolean;

  /** Header title/headline */
  title?: ReactNode;

  /** Left side button (typically back button) */
  leftButton?: InteractionHeaderButtonProps;

  /** Right side buttons (home, activities, favorites, etc.) */
  rightButtons?: InteractionHeaderButtonProps[];

  /** Show a small notification dot on the three-dot menu button */
  showNotificationDot?: boolean;

  /** Phaser button configuration (dev tool) */
  phaserButton?: {
    /** Children element that handles the action */
    children: ReactElement;
    /** Whether phaser is currently active */
    isActive?: boolean;
    /** Title/tooltip text */
    label?: string;
  };

  /** Additional CSS classes */
  className?: string;
};

/**
 * Pure UI component for the interaction header
 * No business logic - all interactions are handled via children elements
 *
 * @example
 * <InteractionHeader
 *   title="My Page"
 *   leftButton={{
 *     iconName: 'arrow-left',
 *     label: 'Back',
 *     children: <Link href="/back" />
 *   }}
 *   rightButtons={[
 *     { iconName: 'home', label: 'Home', children: <Link href="/" /> },
 *     { iconName: 'heart', label: 'Favorites', children: <button onClick={toggleFavorite} /> }
 *   ]}
 * />
 */
export const InteractionHeader: FC<InteractionHeaderProps> = ({
  fallback,
  title,
  leftButton,
  rightButtons = [],
  showNotificationDot,
  phaserButton,
  className,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  // Use isMounted to avoid hydration-errors due to radix-slotting with the IconButton
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Prepare right buttons for rendering in the dropdown menu
  const preparedRightButtons: DropdownMenuOption[] = rightButtons.map(
    button => ({
      children: button.children,
      iconName: button.iconName,
      label: button.label,
      badge: button.badge,
    }),
  );

  if (phaserButton) {
    preparedRightButtons.push({
      iconName: 'tropical-storm',
      label: phaserButton.label ?? 'Phaser',
      children: phaserButton.children,
      badge: phaserButton.isActive ? '✓' : undefined,
    });
  }

  if (fallback) {
    return (
      <div
        className={twMerge(
          'flex justify-center w-full h-[64px]',
          'py-2 md:py-4 overflow-hidden text-marine-high-emphasis',
          className,
        )}
      >
        <div className="relative flex-1 max-w-[1312px]">
          {/* Left button skeleton */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2">
            <Skeleton className="rounded-full size-[48px] md:size-[36px]" />
          </div>

          {/* Title skeleton */}
          <div
            className={twJoin(
              'px-4 h-full flex items-center justify-center',
              'text-center overflow-hidden',
              'mx-[calc(1_*_48px_+_2_*_0.5rem)] md:mx-[36px]',
            )}
          >
            <Skeleton className="h-2 w-[150px]" />
          </div>

          {/* Right button skeleton */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <Skeleton className="rounded-full size-[48px] md:size-[36px]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'flex justify-center w-full h-[64px]',
        'py-2 md:py-4 overflow-hidden text-marine-high-emphasis',
        className,
      )}
    >
      <div className="relative flex-1 max-w-[1312px]">
        {/* Left button */}
        {leftButton && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2">
            <div className="inline-block">
              <IconButton
                iconName={leftButton.iconName ?? 'arrow-left'}
                variant={leftButton.variant ?? 'secondary'}
                size={['md', { md: 'sm' }]}
                on={leftButton.on}
                title={leftButton.label}
                asChild
              >
                {cloneElement(
                  leftButton.children,
                  {},
                  <span className="hidden md:inline">{leftButton.label}</span>,
                )}
              </IconButton>
            </div>
          </div>
        )}

        {/* Title */}
        {title && (
          <div
            className={twJoin(
              'px-4 h-full flex items-center justify-center',
              'text-center overflow-hidden',
              'mx-[calc(1_*_48px_+_2_*_0.5rem)] md:mx-[36px]',
            )}
          >
            <span className="truncate">{title}</span>
          </div>
        )}

        {/* Right buttons */}
        {preparedRightButtons.length > 0 && isMounted && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <div className="relative inline-block">
              <DropdownMenu
                align="end"
                options={preparedRightButtons}
                side="bottom"
              >
                {isOpen => (
                  <IconButton
                    iconName={isOpen ? 'cancel' : 'more-vert'}
                    variant="secondary"
                    size={['md', { md: 'sm' }]}
                  />
                )}
              </DropdownMenu>
              {showNotificationDot && (
                <div className="absolute top-0 right-0 h-3 min-h-3 w-3 min-w-3 rounded-full bg-surface-secondary-100 p-0 pointer-events-none" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
